import React from "react";
import * as styles from "./NewsListing.module.css";
import { ComponentProps } from "../../../types";
import {
  ContentTile,
  ContentTileProps,
} from "../../elements/ContentTile/ContentTile";
import { SectionWrapper } from "../../containers/SectionWrapper";
import { Link } from "../../elements/Link/Link";
import { Article } from "../../../lib/useArticles";
import { KontentImageElement } from "../../elements/KontentImageElement/KontentImageElement";
import { Cta } from "../../elements/Cta/Cta";

interface Props extends ComponentProps {
  articles: Article[];
  setLoadMore: React.Dispatch<React.SetStateAction<boolean>>;
  hasMore?: boolean;
  featuredProgram?: ContentTileProps;
  featuredNewsItem?: ContentTileProps;
  featuredGreenTiles?: ContentTileProps[];
  programs: ContentTileProps[];
}

export const NewsListing: React.FC<Props> = ({
  featuredProgram,
  featuredNewsItem,
  featuredGreenTiles,
  articles,
  hasMore,
  setLoadMore,
  programs,
  ...props
}) => {
  const shortMonths = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "June",
    "July",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
  ];

  const renderGreenTiles = featuredGreenTiles?.map((tile, idx) => {
    return (
      <div className={styles.greenTileContainer} key={`greenTile${idx}`}>
        <ContentTile
          {...tile}
          tileWidth="oneThird"
          green={true}
          hideCTA={true}
        />
      </div>
    );
  });

  const renderArticleListItem = (article: Article, key: number) => {
    const publishDate =
      article.page?.displayPublishDate &&
      new Date(article.page.displayPublishDate);

    const displayDate = publishDate
      ? shortMonths[publishDate?.getMonth()] + " " + publishDate.getDate()
      : "";

    if (!article.page?.url) return null;

    return (
      <div key={`article${key}`}>
        <Link to={article.page?.url} target="_self">
          <div className={styles.articleLink}>
            <div className={styles.articleImage}>
              {article.image.item && (
                <KontentImageElement
                  image={article.image.item}
                  alt={article.image.alt}
                  height={241}
                  width={405}
                  options={{
                    quality:
                      article.image.item.size &&
                      article.image.item.size > 8000000
                        ? 25
                        : 75,
                  }}
                />
              )}
            </div>
            <div>
              <div className={styles.articleCategory}>
                {article.contentCategory &&
                  article.contentCategory != "Uncategorized" && (
                    <span>{article.contentCategory}</span>
                  )}
                {article.page.displayPublishDate && (
                  <span>: {displayDate}</span>
                )}
              </div>
              <h3>{article.page?.title}</h3>
            </div>
          </div>
        </Link>
      </div>
    );
  };

  const renderListing = (articles: Article[]) => {
    return (
      <div className={styles.listing}>
        {articles.map((article, idx) => renderArticleListItem(article, idx))}
      </div>
    );
  };

  const renderPrograms = (programs: ContentTileProps[]) => {
    return (
      <div className={styles.programs}>
        {programs.map((program, idx) => {
          return (
            <ContentTile
              {...program}
              key={`tile${idx}`}
              green={true}
              hideCTA={true}
              tileWidth="full"
            />
          );
        })}
      </div>
    );
  };

  return (
    <SectionWrapper {...props}>
      <div className={styles.newslisting}>
        <div className={styles.grid}>
          {featuredNewsItem && (
            <div className={styles.newsItemContainer}>
              <ContentTile
                {...featuredNewsItem}
                tileWidth="oneThird"
                hideCTA={true}
              />
            </div>
          )}
          {featuredProgram && (
            <div className={styles.programContainer}>
              <ContentTile
                {...featuredProgram}
                tileWidth="oneThird"
                hideCTA={true}
              />
            </div>
          )}
          {featuredGreenTiles?.length && renderGreenTiles}
        </div>
        {renderListing(articles.slice(0, 12))}
        {articles.length < 12 && hasMore && (
          <div className={styles.loadMoreButton}>
            <Cta
              button={true}
              onClick={() => setLoadMore(true)}
              label={"Show More"}
              green={true}
            />
          </div>
        )}
        {renderPrograms(programs.slice(0, 2))}
        {renderListing(articles.slice(12))}
        {articles.length > 12 && hasMore && (
          <div className={styles.loadMoreButton}>
            <Cta
              button={true}
              onClick={() => setLoadMore(true)}
              label={"Show More"}
              green={true}
            />
          </div>
        )}
        {articles.length > 16 &&
          programs.length > 2 &&
          renderPrograms(programs.slice(2, 4))}
      </div>
    </SectionWrapper>
  );
};
